/**
 * Notification Service
 * 
 * Handles all notification/inbox related API calls
 */

import apiClient, { API_ENDPOINTS } from './api';
import { handleApiError } from '../utils/apiErrorHandler';

const notificationService = {
  /**
   * Get list of notifications
   * @param {Object} params - Query parameters (page, limit, read status, etc.)
   * @returns {Promise<Object>} List of notifications
   */
  async getNotifications(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.notifications.list, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch notifications' });
    }
  },

  /**
   * Get notification by ID
   * @param {string} id - Notification ID
   * @returns {Promise<Object>} Notification details
   */
  async getNotification(id) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.notifications.detail(id));
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch notification' });
    }
  },

  /**
   * Mark notification as read
   * @param {string} id - Notification ID
   * @returns {Promise<Object>}
   */
  async markAsRead(id) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.notifications.markRead(id));
      return {
        success: true,
        data: response.data,
        message: 'Notification marked as read',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to mark notification as read' });
    }
  },

  /**
   * Mark all notifications as read
   * @returns {Promise<Object>}
   */
  async markAllAsRead() {
    try {
      const response = await apiClient.post(API_ENDPOINTS.notifications.markAllRead);
      return {
        success: true,
        data: response.data,
        message: 'All notifications marked as read',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to mark all notifications as read' });
    }
  },

  /**
   * Get unread notification count
   * @returns {Promise<Object>} Unread count
   */
  async getUnreadCount() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.notifications.count);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch notification count' });
    }
  },

  /**
   * Get CVO inbox notifications
   * @param {Object} params - Query parameters (page, limit, etc.)
   * @returns {Promise<Object>} CVO inbox data
   */
  async getCVOInbox(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.cvo.inbox, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch CVO inbox' });
    }
  },
};

export default notificationService;

